import React, { useState } from "react";
import { ArrowRight, Check, Shield, Zap } from "lucide-react";
import { z } from "zod";

const FormFieldSchema = z.object({
	label: z.string(),
	placeholder: z.string(),
	options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
});

const CalloutSchema = z.object({
	title: z.string(),
	subtitle: z.string(),
});

const JoinSectionSchema = z.object({
	badgeText: z.string().default("Limited Time - Free Access"),
	header: z.string().default("Ready to Join the"),
	highlightText: z.string().default("PropTech Elite?"),
	description: z
		.string()
		.default(
			"Take the first step towards transforming your PropTech journey. Join a community where every connection matters and every conversation drives innovation."
		),
	benefits: z
		.array(z.string())
		.default([
			"Priority placement on global founders map",
			"Access to exclusive PropTech events and webinars",
			"Direct networking with 500+ verified founders",
			"Beta access to new platform tools and features",
			"Founding member badge and recognition",
			"Featured in community showcases and success stories",
		]),
	formTitle: z.string().default("Secure Your Spot"),
	formSubtitle: z.string().default("Join as a founding member with exclusive benefits"),
	emailField: FormFieldSchema.default({
		label: "Email Address",
		placeholder: "founder@yourcompany.com",
	}),
	companyField: FormFieldSchema.default({
		label: "Company Name",
		placeholder: "Your PropTech Company",
	}),
	roleField: FormFieldSchema.default({
		label: "Your Role",
		placeholder: "Select your role",
		options: [
			{ value: "", label: "Select your role" },
			{ value: "ceo", label: "CEO/Founder" },
			{ value: "cofounder", label: "Co-Founder" },
		],
	}),
	submitButtonText: z.string().default("Join the Founders Circle"),
	termsText: z.string().default("By joining, you agree to our"),
	termsLinkText: z.string().default("Terms of Service"),
	privacyLinkText: z.string().default("Privacy Policy"),
	freeCallout: CalloutSchema.default({
		title: "Free Founding Membership",
		subtitle: "No fees. No commitments. Full access.",
	}),
});

type JoinSectionProps = z.input<typeof JoinSectionSchema>;

const JoinSection: React.FC<JoinSectionProps> = (props = {}) => {
	const data = JoinSectionSchema.parse(props);
	const [email, setEmail] = useState("");
	const [company, setCompany] = useState("");
	const [role, setRole] = useState("");

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		// Handle form submission
		console.log({ email, company, role });
	};

	return (
		<section className="bg-gradient-to-br from-gray-900 via-blue-900 to-teal-900 py-24">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="grid items-center gap-12 lg:grid-cols-2">
					<div className="text-white">
						<div className="mb-6">
							<span className="inline-flex items-center rounded-full border border-orange-400/30 bg-orange-600/20 px-4 py-2 text-sm font-medium text-orange-300 backdrop-blur-sm">
								<Zap className="mr-2 h-4 w-4" />
								{data.badgeText}
							</span>
						</div>

						<h2 className="mb-6 text-3xl font-bold leading-tight md:text-5xl">
							{data.header}
							<span className="block bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">
								{data.highlightText}
							</span>
						</h2>

						<p className="mb-8 text-xl leading-relaxed text-blue-100">{data.description}</p>

						<div className="mb-8 space-y-4">
							{data.benefits.map((benefit, index) => (
								<div key={index} className="flex items-start">
									<div className="mr-4 mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-500">
										<Check className="h-4 w-4 text-white" />
									</div>
									<span className="text-blue-100">{benefit}</span>
								</div>
							))}
						</div>
					</div>

					<div className="rounded-3xl bg-white p-8 shadow-2xl">
						<div className="mb-8 text-center">
							<div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-r from-blue-600 to-teal-600">
								<Shield className="h-8 w-8 text-white" />
							</div>
							<h3 className="mb-2 text-2xl font-bold text-gray-900">{data.formTitle}</h3>
							<p className="text-gray-600">{data.formSubtitle}</p>
						</div>

						<form onSubmit={handleSubmit} className="space-y-6">
							<div>
								<label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">
									{data.emailField.label}
								</label>
								<input
									type="email"
									id="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									className="w-full rounded-xl border border-gray-300 px-4 py-3 transition-all duration-200 focus:border-transparent focus:ring-2 focus:ring-blue-500"
									placeholder={data.emailField.placeholder}
									required
								/>
							</div>

							<div>
								<label htmlFor="company" className="mb-2 block text-sm font-medium text-gray-700">
									{data.companyField.label}
								</label>
								<input
									type="text"
									id="company"
									value={company}
									onChange={(e) => setCompany(e.target.value)}
									className="w-full rounded-xl border border-gray-300 px-4 py-3 transition-all duration-200 focus:border-transparent focus:ring-2 focus:ring-blue-500"
									placeholder={data.companyField.placeholder}
									required
								/>
							</div>

							<div>
								<label htmlFor="role" className="mb-2 block text-sm font-medium text-gray-700">
									{data.roleField.label}
								</label>
								<select
									id="role"
									value={role}
									onChange={(e) => setRole(e.target.value)}
									className="w-full rounded-xl border border-gray-300 px-4 py-3 transition-all duration-200 focus:border-transparent focus:ring-2 focus:ring-blue-500"
									required
								>
									{data.roleField.options?.map((option, index) => (
										<option key={index} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>

							<button
								type="submit"
								className="flex w-full transform items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-teal-600 py-4 text-lg font-semibold text-white transition-all duration-200 hover:-translate-y-1 hover:from-blue-700 hover:to-teal-700 hover:shadow-lg"
							>
								{data.submitButtonText}
								<ArrowRight className="ml-2 h-5 w-5" />
							</button>
						</form>

						<div className="mt-6 text-center">
							<p className="text-sm text-gray-500">
								{data.termsText}{" "}
								<a href="#" className="text-blue-600 hover:underline">
									{data.termsLinkText}
								</a>{" "}
								and{" "}
								<a href="#" className="text-blue-600 hover:underline">
									{data.privacyLinkText}
								</a>
							</p>
						</div>

						<div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-4">
							<div className="flex items-center">
								<div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-green-500">
									<Check className="h-5 w-5 text-white" />
								</div>
								<div>
									<p className="font-semibold text-green-800">{data.freeCallout.title}</p>
									<p className="text-sm text-green-600">{data.freeCallout.subtitle}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default JoinSection;
